import React, { useEffect, useMemo } from 'react';
import {
  FaShoppingBag,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { getAvailableMyorders, getMyOrders } from '../redux/pos/posSlice';
import { getCurrentTenant, getCurrentUser } from '../redux/auth/authSlice';
import { useThemeColors } from '../hooks/useThemeColors';
import { useNavigate } from 'react-router-dom';

const MyOrders = ({ darkMode }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Hook se central colors le rahe hain
  const { themeBg, cardBg, textColor, subTextColor, borderColor, isDark } =
    useThemeColors(darkMode);

  const user = useSelector(getCurrentUser);
  const myOrderData = useSelector(getAvailableMyorders);
  const currentTenant = useSelector(getCurrentTenant);

  useEffect(() => {
    if (user?.referenceId && currentTenant?.id) {
      dispatch(
        getMyOrders({
          userId: user.referenceId,
          tenantId: currentTenant.id,
        })
      );
    }
  }, [dispatch, user?.referenceId, currentTenant?.id]);

  const ordersList = useMemo(() => {
    return myOrderData?.items || [];
  }, [myOrderData]);
  console.log('ordersList', ordersList);

  if (!user || !currentTenant) {
    return (
      <div
        className="container-fluid d-flex align-items-center justify-content-center"
        style={{ minHeight: '80vh', backgroundColor: themeBg }}
      >
        <div
          className="text-center p-5 rounded-4 shadow-sm"
          style={{
            backgroundColor: cardBg,
            border: `1px solid ${borderColor}`,
          }}
        >
          <h5 style={{ color: textColor }}>
            Please login to view your orders.
          </h5>
          <button className="btn btn-danger mt-3 rounded-pill px-4">
            Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="my-orders-page py-4 py-sm-5"
      style={{
        backgroundColor: themeBg,
        minHeight: '100vh',
        transition: '0.3s ease',
      }}
    >
      <div className="container pb-5">
        {/* Header Section */}
        <div className="row mb-5 align-items-center">
          <div className="col-md-8">
            <div className="d-flex align-items-center mb-1">
              <div className="bg-danger p-2 rounded-3 me-3 shadow-sm">
                <FaShoppingBag className="text-white" size={22} />
              </div>
              <h2
                className="fw-bold mb-0"
                style={{ color: textColor, letterSpacing: '-1px' }}
              >
                Order History
              </h2>
            </div>
            <p className="mb-0" style={{ color: subTextColor }}>
              Track and manage your previous delicious meals
            </p>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            {ordersList.length > 0 ? (
              ordersList.map((group, index) => (
                <div
                  key={group.preInvoice?.id || index}
                  className="card mb-4 shadow-sm border-0 overflow-hidden"
                  style={{
                    backgroundColor: cardBg,
                    borderRadius: '20px',
                    border: `1px solid ${borderColor}`,
                    transition: 'transform 0.3s ease',
                  }}
                >
                  {/* Order Card Header */}
                  <div
                    className="card-header border-0 py-3 px-4 d-flex justify-content-between align-items-center"
                    style={{
                      backgroundColor: isDark
                        ? 'rgba(255,255,255,0.03)'
                        : 'rgba(0,0,0,0.02)',
                    }}
                  >
                    <div
                      className="d-flex align-items-center fw-bold"
                      style={{ color: textColor, fontSize: '0.9rem' }}
                    >
                      <FaCalendarAlt className="me-2 text-danger" />
                      {group.dateString}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="badge rounded-pill px-3 py-2 fw-bold"
                        style={{
                          fontSize: '10px',
                          backgroundColor: group.preInvoice?.processed
                            ? 'rgba(40, 167, 69, 0.1)'
                            : 'rgba(228, 0, 43, 0.1)',
                          color: group.preInvoice?.processed
                            ? '#28a745'
                            : '#e4002b',
                          border: `1px solid ${
                            group.preInvoice?.processed ? '#28a745' : '#e4002b'
                          }`,
                        }}
                      >
                        {group.preInvoice?.processed
                          ? '✓ Processed'
                          : '● Pending'}
                      </span>
                    </div>
                  </div>

                  {/* Order Items Body */}
                  <div className="card-body p-4">
                    {group.orders &&
                      group.orders.map((order, orderIdx) => (
                        <div
                          key={orderIdx}
                          className={`py-3 ${
                            orderIdx !== group.orders.length - 1
                              ? 'border-bottom'
                              : ''
                          }`}
                          style={{ borderColor: borderColor }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <div className="bg-danger bg-opacity-10 rounded-circle p-2 me-3 d-none d-sm-block">
                                <FaClock size={14} className="text-danger" />
                              </div>
                              <div>
                                <h6
                                  className="mb-1 fw-bold"
                                  style={{ color: textColor }}
                                >
                                  {order.name}
                                </h6>
                                <p
                                  className="mb-0 small"
                                  style={{ color: subTextColor }}
                                >
                                  Qty:{' '}
                                  <span
                                    className="fw-bold"
                                    style={{ color: textColor }}
                                  >
                                    {order.quantity}
                                  </span>
                                  <span className="mx-2 opacity-50">|</span>
                                  Price: Rs.{' '}
                                  {Number(order.price).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <span
                              className="fw-bold fs-6"
                              style={{ color: textColor }}
                            >
                              Rs.{' '}
                              {(
                                Number(order.price) * Number(order.quantity)
                              ).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* Total Footer */}
                  <div
                    className="card-footer border-0 py-3 px-4 d-flex justify-content-between align-items-center"
                    style={{
                      backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#fff',
                      borderTop: `1px solid ${borderColor}`,
                    }}
                  >
                    <div className="d-flex flex-column">
                      <span
                        className="small text-uppercase fw-bold opacity-50"
                        style={{
                          color: subTextColor,
                          fontSize: '0.65rem',
                          letterSpacing: '1px',
                        }}
                      >
                        Grand Total
                      </span>
                      <h4 className="fw-bold text-danger mb-0">
                        Rs.{' '}
                        {Number(
                          group.preInvoice?.totalAmount || 0
                        ).toLocaleString()}
                      </h4>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              /* Empty State */
              <div
                className="text-center py-5 rounded-5"
                style={{
                  backgroundColor: cardBg,
                  border: `2px dashed ${borderColor}`,
                }}
              >
                <div
                  className="mb-4 d-inline-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: '100px',
                    height: '100px',
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.03)',
                  }}
                >
                  <FaShoppingBag size={50} className="text-muted opacity-25" />
                </div>
                <h4 className="fw-bold mb-2" style={{ color: textColor }}>
                  No orders yet
                </h4>
                <p
                  className="mb-4 mx-auto"
                  style={{ color: subTextColor, maxWidth: '350px' }}
                >
                  Hungry? Your previous orders will appear here once you place
                  them.
                </p>
                <button
                  className="btn btn-danger px-5 py-3 rounded-pill fw-bold shadow-sm"
                  onClick={() => navigate('/')}
                >
                  Start Your First Order
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyOrders;
